'use client';

import { useAsyncError } from '@/context/async-error-boundary';
import { getUserProfileQueryFn } from '@/lib/api';
import API from '@/lib/axios-client';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

const useAuth = () => {
    const [globalError, setGlobalError] = useState(null);
    const { captureError } = useAsyncError();
    const query = useQuery({
        queryKey: ['authUser'],
        queryFn: getUserProfileQueryFn,
        retry: false,
        throwOnError: (
            error: Error & { errorCode?: string; code?: string } & string,
        ) => {
            if (globalError || error === 'Network Error') {
                captureError(globalError || error);
            }
            return false;
        },

        staleTime: 5 * 60 * 1000, // 5 minutes
        refetchOnWindowFocus: 'always',
    });
    useEffect(() => {
        const reqAPI = API.interceptors.request.use(
            (config) => {
                setGlobalError(null);
                return config;
            },
            (error) => Promise.reject(error),
        );

        const resAPI = API.interceptors.response.use(
            (response) => response,
            (error) => {
                if (error.status === 500) {
                    error.message =
                        'Internal Server Error\nPlease wait a few minutes before you try again.';
                    setGlobalError(error);
                }
                if (error.code === 'ECONNABORTED') {
                    error.message =
                        'Request timed out after 30 seconds.\nPlease wait a few minutes before you try again.';
                    setGlobalError(error);
                }
                if (error.code === 'ERR_NETWORK') {
                    error.message = 'Network error, please try again later.';
                    setGlobalError(error);
                }
                return Promise.reject(error);
            },
        );

        return () => {
            API.interceptors.response.eject(resAPI);
            API.interceptors.request.eject(reqAPI);
        };
    }, []);
    return query;
};

export default useAuth;
